import React from "react";
import {
  Card,
  Group,
  Text,
  Checkbox,
  Badge,
  Stack,
  useMantineTheme,
  Box,
} from "@mantine/core";
import { AppButton, AppIcon } from "../../../generic";

export interface ParsedEvent {
  /** Temporary ID for the parsed event (before saving to API) */
  tempId: string;
  /** Event title/summary */
  title?: string;
  /** Event description */
  description?: string;
  /** Event start date/time */
  startDateTime?: string;
  /** Event end date/time */
  endDateTime?: string;
  /** Event location */
  location?: string;
  /** All-day event flag */
  isAllDay?: boolean;
  /** Event organizer */
  organizer?: string;
  /** Event attendees */
  attendees?: string[];
  /** Event status (confirmed, tentative, cancelled) */
  status?: "confirmed" | "tentative" | "cancelled";
  /** Event transparency (busy, free) */
  transparency?: "busy" | "free";
  /** Recurrence rule */
  recurrenceRule?: string;
  /** Original iCal data for debugging */
  originalData?: any;
  uid?: string;
}

export interface EventPreviewCardProps {
  /** The parsed event data */
  event: ParsedEvent;
  /** Index of the event in the list */
  index: number;
  /** Whether the event is selected for bulk operations */
  selected?: boolean;
  /** Whether the card is disabled */
  disabled?: boolean;
  /** Whether an add operation is in progress for this event */
  adding?: boolean;
  /** Whether bulk operations are enabled */
  enableSelection?: boolean;
  /** Callback when the selection checkbox is toggled */
  onSelectionChange?: (eventId: string, selected: boolean) => void;
  /** Callback when the "Add" button is clicked */
  onAdd?: (event: ParsedEvent) => void;
  /** Custom styles from virtualization */
  style?: React.CSSProperties;
  /** ARIA attributes for accessibility */
  ariaAttributes?: {
    "aria-posinset": number;
    "aria-setsize": number;
    role: "listitem";
  };
}

export const EventPreviewCard: React.FC<EventPreviewCardProps> = ({
  event,
  index,
  selected = false,
  disabled = false,
  adding = false,
  enableSelection = true,
  onSelectionChange,
  onAdd,
  style,
  ariaAttributes,
}) => {
  const theme = useMantineTheme();

  // Format date/time for display
  const formatDateTime = (dateTime: string | undefined): string => {
    if (!dateTime) return "No date";

    try {
      const date = new Date(dateTime);
      return date.toLocaleString("en-CA", {
        weekday: "short",
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return dateTime;
    }
  };

  // Format date range for display
  const formatDateRange = (): string => {
    if (event.isAllDay) {
      if (event.startDateTime && event.endDateTime) {
        const start = new Date(event.startDateTime);
        const end = new Date(event.endDateTime);

        // Check if same day
        if (start.toDateString() === end.toDateString()) {
          return `All day • ${start.toLocaleDateString("en-CA", {
            weekday: "short",
            month: "short",
            day: "numeric",
            year: "numeric",
          })}`;
        } else {
          return `All day • ${start.toLocaleDateString("en-CA", {
            month: "short",
            day: "numeric",
          })} - ${end.toLocaleDateString("en-CA", {
            month: "short",
            day: "numeric",
            year: "numeric",
          })}`;
        }
      }
      return "All day";
    }

    if (event.startDateTime && event.endDateTime) {
      const start = new Date(event.startDateTime);
      const end = new Date(event.endDateTime);

      // Check if same day
      if (start.toDateString() === end.toDateString()) {
        return `${start.toLocaleDateString("en-CA", {
          weekday: "short",
          month: "short",
          day: "numeric",
        })} • ${start.toLocaleTimeString("en-CA", {
          hour: "2-digit",
          minute: "2-digit",
        })} - ${end.toLocaleTimeString("en-CA", {
          hour: "2-digit",
          minute: "2-digit",
        })}`;
      } else {
        return `${formatDateTime(event.startDateTime)} - ${formatDateTime(
          event.endDateTime
        )}`;
      }
    }

    if (event.startDateTime) {
      return formatDateTime(event.startDateTime);
    }

    return "No date specified";
  };

  // Get status badge color
  const getStatusColor = (): string => {
    switch (event.status) {
      case "confirmed":
        return "green";
      case "tentative":
        return "yellow";
      case "cancelled":
        return "red";
      default:
        return "gray";
    }
  };

  // Handle selection change
  const handleSelectionChange = (checked: boolean) => {
    onSelectionChange?.(event.tempId, checked);
  };

  // Handle add button click
  const handleAdd = () => {
    onAdd?.(event);
  };

  // Get card background based on selection state
  const getCardBackground = () => {
    if (selected) {
      return theme.colors.blue[0];
    }
    if (disabled) {
      return theme.colors.gray[0];
    }
    return theme.white;
  };

  return (
    <Box style={style} {...ariaAttributes} mb="lg">
      <Card
        shadow="sm"
        padding="md"
        radius="md"
        withBorder
        style={{
          backgroundColor: getCardBackground(),
          borderColor: selected
            ? theme.colors.blue[3]
            : disabled
            ? theme.colors.gray[3]
            : theme.colors.gray[2],
          borderWidth: selected ? 2 : 1,
          opacity: disabled ? 0.6 : 1,
          margin: theme.spacing.xs,
        }}
      >
        <Stack gap="sm">
          {/* Header with selection and action */}
          <Group justify="space-between" align="flex-start">
            <Group gap="sm" style={{ flex: 1 }}>
              {enableSelection && (
                <Checkbox
                  checked={selected}
                  onChange={(event) =>
                    handleSelectionChange(event.currentTarget.checked)
                  }
                  disabled={disabled}
                  size="sm"
                />
              )}
              <Stack gap={4} style={{ flex: 1 }}>
                <Text
                  fw={600}
                  size="sm"
                  lineClamp={2}
                  c={disabled ? "dimmed" : ""}
                >
                  {event.title || "Untitled Event"}
                </Text>
                <Group gap="xs">
                  <AppIcon
                    iconName="Calendar"
                    size={14}
                    style={{ color: theme.colors.gray[6] }}
                  />
                  <Text size="xs" c="dimmed">
                    {formatDateRange()}
                  </Text>
                </Group>
              </Stack>
            </Group>

            <AppButton
              size="xs"
              iconName="Plus"
              onClick={handleAdd}
              disabled={disabled || adding}
              loading={adding}
            >
              Add
            </AppButton>
          </Group>

          {/* Event details */}
          <Stack gap="xs">
            {event.location && (
              <Group gap="xs">
                <AppIcon
                  iconName="MapPin"
                  size={14}
                  style={{ color: theme.colors.gray[6] }}
                />
                <Text size="xs" c="dimmed" lineClamp={1}>
                  {event.location}
                </Text>
              </Group>
            )}

            {event.description && (
              <Text size="xs" c="dimmed" lineClamp={2}>
                {event.description}
              </Text>
            )}

            {event.organizer && (
              <Group gap="xs">
                <AppIcon
                  iconName="User"
                  size={14}
                  style={{ color: theme.colors.gray[6] }}
                />
                <Text size="xs" c="dimmed" lineClamp={1}>
                  {event.organizer}
                </Text>
              </Group>
            )}

            {event.attendees && event.attendees.length > 0 && (
              <Group gap="xs">
                <AppIcon
                  iconName="Users"
                  size={14}
                  style={{ color: theme.colors.gray[6] }}
                />
                <Text size="xs" c="dimmed">
                  {event.attendees.length} attendee
                  {event.attendees.length !== 1 ? "s" : ""}
                </Text>
              </Group>
            )}
          </Stack>

          {/* Status and additional info */}
          <Group justify="space-between" align="center">
            <Group gap="xs">
              {event.status && (
                <Badge size="xs" color={getStatusColor()} variant="light">
                  {event.status}
                </Badge>
              )}

              {event.uid && (
                <Badge size="xs" color={"yellow"} variant="light">
                  {event.uid}
                </Badge>
              )}

              {event.transparency === "free" && (
                <Badge size="xs" color="gray" variant="light">
                  Free
                </Badge>
              )}

              {event.isAllDay && (
                <Badge size="xs" color="blue" variant="light">
                  All Day
                </Badge>
              )}

              {event.recurrenceRule && (
                <Badge size="xs" color="purple" variant="light">
                  Recurring
                </Badge>
              )}
            </Group>

            <Text size="xs" c="dimmed">
              #{index + 1}
            </Text>
          </Group>
        </Stack>
      </Card>
    </Box>
  );
};
